import { useTheme } from "@/src/styles/theme-provider";
import { Bag2, MedalStar, Star1 } from "iconsax-react-nativejs";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Animated, Text, View } from "react-native";

interface SeasonBalanceProps {
	seasonAllowance?: number;
	starsReceived?: number;
	rewardValue?: string;
	isLoading?: boolean;
}

const StatCardSkeleton = () => {
	const opacity = useRef(new Animated.Value(0.3)).current;

	useEffect(() => {
		const loop = Animated.loop(
			Animated.sequence([
				Animated.timing(opacity, { toValue: 1, duration: 800, useNativeDriver: true }),
				Animated.timing(opacity, { toValue: 0.3, duration: 800, useNativeDriver: true }),
			]),
		);
		loop.start();
		return () => loop.stop();
	}, [opacity]);

	return (
		<Animated.View
			style={{ opacity }}
			className="flex-1 h-[110px] bg-ash rounded-xl md:border md:border-ash-3"
		/>
	);
};

const SeasonBalance = ({
	seasonAllowance = 0,
	starsReceived = 0,
	rewardValue,
	isLoading = false,
}: SeasonBalanceProps) => {
	const { t } = useTranslation();
	const { themeVars } = useTheme();

	if (isLoading) {
		return (
			<View className="flex-col gap-3 md:flex-row md:gap-4">
				<StatCardSkeleton />
				<StatCardSkeleton />
			</View>
		);
	}

	return (
		<View className="flex-col gap-3 md:flex-row md:gap-4">
			{/* Stars received this season */}
			<View className="flex-1 flex-row justify-between items-start bg-ash rounded-xl p-4 md:border md:border-ash-3">
				<View className="flex-col">
					<Text className="text-xs text-ash-2 mb-2">
						{t("LABEL_STARS_RECEIVED_THIS_SEASON")}
					</Text>
					<View className="flex-row items-center gap-1.5 mb-3">
						<Star1 size={22} color={themeVars["--color-onyx-default"] as string} />
						<Text className="text-3xl font-bold text-primary">{starsReceived}</Text>
					</View>
					{rewardValue != null && (
						<Text className="text-xs font-medium text-onyx">
							{t("LABEL_REWARD_AMOUNT", { amount: rewardValue })}
						</Text>
					)}
				</View>
				<View className="p-3 rounded-xl bg-primary">
					<MedalStar size={24} color={themeVars["--color-chalk-default"] as string} />
				</View>
			</View>

			{/* Season allowance left */}
			<View className="flex-1 flex-row justify-between items-start bg-ash rounded-xl p-4 md:border md:border-ash-3">
				<View className="flex-col">
					<Text className="text-xs text-ash-2 mb-2">
						{t("LABEL_SEASON_ALLOWANCE_LEFT")}
					</Text>
					<View className="flex-row items-center gap-1.5 mb-3">
						<Star1 size={22} color={themeVars["--color-onyx-default"] as string} />
						<Text className="text-3xl font-bold text-onyx">{seasonAllowance}</Text>
					</View>
					<Text className="text-xs text-ash-2">
						{t("LABEL_STARS_TO_GIVE_COLLEAGUES")}
					</Text>
				</View>
				<View className="p-3 rounded-xl bg-primary">
					<Bag2 size={24} color={themeVars["--color-chalk-default"] as string} />
				</View>
			</View>
		</View>
	);
};

export default SeasonBalance;
